import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { useMemo, useState } from 'react';
import colors from 'assets/scss/colors.module.scss';
import { TimeSeriesChart, ITimeSeriesChartConfig } from 'app/components/common/charts/TimeSeriesChart';
import { RadioButtonList, IRadioButtonListItem } from 'app/components/common/RadioButtonList';

interface IMazHistoryChart {
	agtId?: number;
	agtIds?: number[];
}

interface MazChartData {
	bm: string;
	bmRaw: number;
	mazBestand: number;
	kndBestand: number;
	mazNeu: number;
	kndNeu: number;
	anzKnd: number;
}

type CustomerType = 'bestand' | 'neu';

export const MazHistoryChart = observer((props: IMazHistoryChart) => {
	const { agtDigitalreportUiStore } = useStore();
	const { agtId, agtIds } = props;
	const [customerType, setCustomerType] = useState<CustomerType>('bestand');

	// Toggle buttons configuration
	const toggleButtons: IRadioButtonListItem[] = [
		{ label: 'Bestandskunden', value: 'bestand' },
		{ label: 'Neukunden', value: 'neu' },
	];

	// Build chart configuration
	const chartConfig: ITimeSeriesChartConfig<MazChartData> = useMemo(
		() => ({
			title: `mAZ Entwicklung ${customerType === 'bestand' ? 'Bestandskunden' : 'Neukunden'} (letzte 12 Monate)`,
			tag: 'mAZ',
			tagColor: 'primary',
			height: 400,

			xAxisField: 'bm',
			xAxisRotation: 45,

			// Define Y-axes
			yAxes: [
				{
					name: 'Anzahl Kunden',
					position: 'left',
				},
				{
					name: 'Durchdringung (%)',
					position: 'right',
					max: 100,
					formatter: (v) => `${v}%`,
					valueType: 'percentage',
					splitLine: false,
				},
			],

			// Define derived fields (calculated at render time)
			derivedFields: [
				{
					fieldName: 'penetration',
					calculate: (d) => {
						if (customerType === 'bestand') {
							return d.kndBestand > 0 ? (d.mazBestand / d.kndBestand) * 100 : 0;
						} else {
							return d.kndNeu > 0 ? (d.mazNeu / d.kndNeu) * 100 : 0;
						}
					},
				},
			],

			// Define series
			series: [
				{
					name: customerType === 'bestand' ? 'mAZ Bestandskunden (18-70)' : 'mAZ Neukunden',
					dataField: customerType === 'bestand' ? 'mazBestand' : 'mazNeu',
					yAxisIndex: 0,
					chartType: 'bar',
					stack: 'total',
					color: colors.blue,
				},
				{
					name: customerType === 'bestand' ? 'Kunden Gesamt 18-70' : 'Neukunden Gesamt',
					dataField: customerType === 'bestand' ? 'kndBestand' : 'kndNeu',
					yAxisIndex: 0,
					chartType: 'bar',
					stack: 'total',
					color: colors.softSky,
				},
				{
					name: `Durchdringung ${customerType === 'bestand' ? 'Bestand' : 'Neukunden'} (%)`,
					dataField: 'penetration',
					yAxisIndex: 1,
					chartType: 'line',
					smooth: true,
					color: colors.orange,
					symbolSize: 6,
					lineStyle: { width: 3 },
					markLines: [
						{
							yValue: customerType === 'bestand' ? 60 : 75,
							label: `Ambitionsniveau ${customerType === 'bestand' ? 60 : 75}%`,
							color: colors.black,
							lineType: 'dashed',
						},
					],
				},
			],

			// Data loader function
			dataLoader: async (ids) => {
				if (Array.isArray(ids)) {
					return agtDigitalreportUiStore.loadMazHistoryLast12MonthsAggregated(ids);
				} else {
					return agtDigitalreportUiStore.loadMazHistoryLast12Months(ids);
				}
			},
		}),
		[agtDigitalreportUiStore, customerType],
	);

	return (
		<div>
			<div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
				<RadioButtonList value={customerType} buttons={toggleButtons} onSelect={(value) => setCustomerType(value as CustomerType)} />
			</div>
			<TimeSeriesChart config={chartConfig} agtId={agtId} agtIds={agtIds} />
		</div>
	);
});
